import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Pagination,
} from '@gapartners/ui';
import { api } from '@/store/api';
import type { CustomerCase } from '@/types/customer';

export interface LinkCaseDialogProps {
  workspaceId: string | null;
  open: boolean;
  onClose: () => void;
  onSelect: (caseItem: CustomerCase) => void;
  /** Internal ClientSpace ID of a case to hide (the case being linked from). */
  excludeCaseId?: number | null;
  /** Disables confirmation while the selection is being applied. */
  isSubmitting?: boolean;
}

export function LinkCaseDialog(props: LinkCaseDialogProps) {
  const {
    workspaceId,
    open,
    onClose,
    onSelect,
    excludeCaseId = null,
    isSubmitting = false,
  } = props;
  const { t } = useTranslation();
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { useGetCustomerCasesQuery } = api;

  const {
    data: customerCases,
    isLoading: isCasesLoading,
    error: casesError,
  } = useGetCustomerCasesQuery(
    {
      workSpaceID: workspaceId ?? '',
      page: currentPage,
      pageSize,
    },
    { skip: !open || !workspaceId }
  );

  const cases = (customerCases?.cases ?? []).filter(
    caseItem => excludeCaseId == null || caseItem.id !== excludeCaseId
  );
  const totalCount = customerCases?.totalCount ?? 0;

  const totalPages = Math.ceil(totalCount / pageSize);

  // Show error if API failed and no cases were loaded
  const displayError = casesError && cases.length === 0;

  useEffect(() => {
    if (open) {
      setCurrentPage(1);
      setSelectedCaseId(null);
    }
  }, [open]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
    setSelectedCaseId(null);
  };

  const handleSelectCase = (caseItem: CustomerCase) => {
    setSelectedCaseId(caseItem.caseId);
  };

  const handleConfirm = () => {
    if (!selectedCaseId) return;
    const selectedCase = cases.find(c => c.caseId === selectedCaseId);
    if (!selectedCase) return;
    onSelect(selectedCase);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('components.contactCenter.linkToRelatedCase')}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {isCasesLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}

        {displayError && (
          <Typography color="error.main" variant="body2">
            {t('components.contactCenter.errorLoadingCases')}
          </Typography>
        )}

        {!isCasesLoading && !displayError && cases.length === 0 && (
          <Typography color="text.secondary" variant="body2">
            {t('components.contactCenter.noCasesFound')}
          </Typography>
        )}

        {!isCasesLoading && !displayError && cases.length > 0 && (
          <List sx={{ width: '100%' }}>
            {cases.map((caseItem, index) => (
              <Box key={caseItem.caseId}>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={selectedCaseId === caseItem.caseId}
                    onClick={() => handleSelectCase(caseItem)}
                    sx={{
                      bgcolor:
                        selectedCaseId === caseItem.caseId
                          ? 'action.selected'
                          : 'transparent',
                    }}
                  >
                    <ListItemText
                      primary={
                        caseItem.description
                          ? `${caseItem.caseId} — ${caseItem.description}`
                          : `${t('components.contactCenter.case')} ${caseItem.caseId}`
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" display="block">
                            {t('components.contactCenter.status')}:{' '}
                            {caseItem.status}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {t('components.contactCenter.category')}:{' '}
                            {caseItem.category || '—'}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {t('components.contactCenter.subCaseType')}:{' '}
                            {caseItem.subCaseType || '—'}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {t('components.contactCenter.assignedTo')}:{' '}
                            {caseItem.assignedTo || '—'}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {t('components.contactCenter.reportedByName')}:{' '}
                            {caseItem.reportedByName || '—'}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {t('components.contactCenter.employeeName')}:{' '}
                            {caseItem.employeeName || '—'}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {index < cases.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        )}

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selectedCaseId || isSubmitting}
        >
          {t('components.contactCenter.linkToRelatedCase')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
